import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    List,
    ListItem,
    Stack,
    Typography,
    Modal
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import GiftCardDesign from './GiftCardDesign';
import GiftCardDesignPreview from './GiftCardDesignPreview';
import { hideGiftDesignPanel, isUserWantDesignGiftCard, showGiftDesignPanel } from '../../store/giftcardDesign/action';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {
        xs: '90vw',  // 90% of viewport width on extra small devices
        sm: '80vw',  // 80% on small devices
        md: '95%',  // 70% on medium
        lg: '95%',   // 95% on large (as per your original)
        xl: '95%'    // 95% on extra large
    },
    height: {
        xs: '90vh',  // 90% of viewport height on mobile
        sm: '85vh',  // 85% on small
        md: '85vh',  // 85% on medium
        lg: '95%',   // 95% on large (as per your original)
        xl: '95%'    // 95% on extra large
    },
    maxWidth: 1200, // prevents modal from becoming too wide on large screens
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: 4,
    p: {
        xs: 2,  // smaller padding on mobile
        sm: 3,  // medium padding on tablets
        md: 4   // full padding on desktop
    },
    overflowY: 'auto',
};

const GiftCardOption = () => {
    const [isGiftCardSelected, setIsGiftCardSelected] = useState(false);
    const [isDesignModalOpen, setIsDesignModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [hasModalBeenOpened, setHasModalBeenOpened] = useState(false);
    const [hasDesignBeenSaved, setHasDesignBeenSaved] = useState(false);
    const [isUserWorkingOnDesign, setIsUserWorkingOnDesign] = useState(false);

    const PRODUCTDETAILS = useSelector((state) => state.ProductDetail?.allData);
    const GIFTDESIGNPANEL = useSelector((state) => state.GiftCardDesign);
    const dispatch = useDispatch();

    const toggleGiftCardSelection = () => {
        if (!isGiftCardSelected) {
            setIsGiftCardSelected(true);
            dispatch(showGiftDesignPanel());
            dispatch(isUserWantDesignGiftCard(true));
            setIsUserWorkingOnDesign(true);
            openDesignModal();
            return;
        }


        setIsConfirmModalOpen(true);
    };

    const handleConfirmUnselect = () => {
        setIsGiftCardSelected(false);
        dispatch(isUserWantDesignGiftCard(false));
        setHasDesignBeenSaved(false);
        setIsConfirmModalOpen(false);
        setIsUserWorkingOnDesign(false);
    };

    const handleCancelUnselect = () => {
        setIsConfirmModalOpen(false);
        // Keep the checkbox checked when canceling
        setIsGiftCardSelected(true);
    };

    const openDesignModal = () => {
        setIsDesignModalOpen(true);
        setHasModalBeenOpened(true);
        dispatch(showGiftDesignPanel());
    };

    const closeDesignModal = () => {
        // setIsDesignModalOpen(false);
        dispatch(hideGiftDesignPanel());

        // Only uncheck if modal is closed without saving
        // if (!hasDesignBeenSaved) {
        //     // setIsGiftCardSelected(false);
        //     // dispatch(isUserWantDesignGiftCard(false));
        // }
    };

    const handleEditClick = () => {
        openDesignModal();
    };

    const handleSaveDesign = () => {
        setHasDesignBeenSaved(true);
        closeDesignModal();
    };

    // Add this effect to handle browser back button or other modal closures
    // useEffect(() => {
    //     if (!isDesignModalOpen && !hasDesignBeenSaved && isGiftCardSelected) {
    //         setIsGiftCardSelected(false);
    //         dispatch(isUserWantDesignGiftCard(false));
    //     }
    // }, [isDesignModalOpen, hasDesignBeenSaved, isGiftCardSelected, dispatch]);

    return (
        <>
            <Box sx={{ mt: { xs: 0, lg: 2 } }}>
                <HeaderSection
                    isModalOpen={GIFTDESIGNPANEL?.isShowGiftDesignModel}
                    hasModalBeenOpened={hasModalBeenOpened}
                    isGiftCardSelected={isGiftCardSelected}
                    onEditClick={handleEditClick}
                    onCloseClick={closeDesignModal}

                    isUserWorkingOnDesign={isUserWorkingOnDesign}
                />

                <GiftCardCheckbox
                    isSelected={isGiftCardSelected}
                    onToggle={toggleGiftCardSelection}
                    isDisabled={false}
                />
            </Box>

            <DesignModal
                isOpen={GIFTDESIGNPANEL?.isShowGiftDesignModel}
                onClose={closeDesignModal}
                onSave={handleSaveDesign}
            />

            {/* Confirmation Modal */}
            <Modal
                open={isConfirmModalOpen}
                onClose={handleCancelUnselect}
                aria-labelledby="confirm-unselect-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    p: 4,
                }}>
                    <Typography variant="h6" gutterBottom>
                        Confirm Unselection
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        Are you sure you want to unselect this option? Your saved design data will be removed.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleCancelUnselect}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmUnselect}
                            color="error"
                        >
                            Confirm
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

// Sub-components
const HeaderSection = ({ isModalOpen, onEditClick, onCloseClick, isUserWorkingOnDesign }) => (
    <Stack
        direction="row"
        spacing={2}
        sx={{
            justifyContent: "space-between",
            alignItems: "center",
            pr: 0.5,
            my: { xs: 1, lg: 0.5 },
            height: 24,
        }}
    >
        <Typography
            variant="body1"
            sx={{
                color: { xs: "primary.main", sm: "textPrimary.main" },
                fontSize: { xs: "1.2rem", sm: "13.5px", lg: "15px" },
                fontWeight: 600,
                textAlign: { xs: "center", lg: "left" },
                borderBottom: { xs: 1, lg: 0 },
            }}
        >
            Gift a Friend
        </Typography>

        {isUserWorkingOnDesign && (
            <Button
                variant='text'
                onClick={isModalOpen ? onCloseClick : onEditClick}
                sx={{
                    py: 0,
                    backgroundColor: 'secondary.light',
                    color: 'secondary.main',
                    fontSize: { xs: "1.2rem", sm: "13.5px" },
                    fontWeight: 400,
                    textTransform: 'capitalize',
                    px: 2,
                    cursor: 'pointer',
                    "&:hover": { backgroundColor: 'secondary.light' }
                }}
            >
                {isModalOpen ? 'Close' : 'Edit'}
            </Button>
        )}
    </Stack>
);

const GiftCardCheckbox = ({ isSelected, onToggle, isDisabled }) => (
    <Box
        sx={{
            borderRadius: 2,
            minHeight: "30px",
            p: 0,
            backgroundColor: "secondary.light"
        }}
    >
        <List sx={{ py: 0 }}>
            <ListItem sx={{ justifyContent: "space-between" }}>
                <Typography variant="subtitle1"
                    sx={{
                        color: "textPrimary.main",
                        fontWeight: 500,
                        fontSize: { xs: ".9rem", sm: "14px" },
                    }}>
                    Do you want to Gift a Friend?
                </Typography>
                <Checkbox
                    checked={isSelected}
                    onChange={onToggle}
                    disabled={isDisabled}
                    inputProps={{ 'aria-label': 'gift card selection' }}
                />
            </ListItem>
        </List>
    </Box>
);

const DesignModal = ({ isOpen, onClose }) => (
    <Modal
        open={isOpen}
        // onClose={onClose}
        aria-labelledby="gift-card-design-modal"
    >
        <Box sx={modalStyle}>
            <ModalHeader onClose={onClose} />
            <Box
                sx={{
                    display: {
                        sm: 'flex',
                        md: 'flex',
                    },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    height: {
                        xs: 'calc(100% - 40px)',  // adjust for header on mobile
                        sm: '100%',
                    },
                    width: {
                        sm: '100%',
                    },
                    mb: 4
                }}
            >
                <GiftCardDesignPreview />

                <Stack direction='column'>
                    <GiftCardDesign />

                    <Button
                        variant='contained'
                        fullWidth
                        endIcon={<TrendingFlatIcon />}
                        sx={{
                            py: 1,
                            maxWidth: 170,
                            color: 'dark.main',
                            backgroundColor: 'background.main3',
                            borderRadius: 1,
                            boxShadow: 0,
                            textTransform: 'capitalize',
                            fontSize: '1.225rem',
                            fontWeight: 400,
                            mt: 2,

                            "&:hover": {
                                backgroundColor: 'background.main3',
                                boxShadow: 1,
                            }
                        }}

                        onClick={() => onClose()}
                    >
                        Continue
                    </Button>
                </Stack>
            </Box>
        </Box>
    </Modal>
);

const ModalHeader = ({ onClose }) => (
    <Stack
        direction={'row'}
        justifyContent={"space-between"}
        alignItems="center"
        sx={{ mb: 2 }}
    >
        <Typography
            variant='h3'
            sx={{
                mb: 1,
                fontSize: { lg: '1.325rem' }
            }}
        >
            Design Your Gift Card
        </Typography>
        <IconButton
            aria-label="close modal"
            sx={{
                color: 'text.secondary',
                backgroundColor: 'action.hover',
                borderRadius: 2,
                '&:hover': {
                    backgroundColor: 'action.hover',
                    color: 'text.primary',
                    boxShadow: 2,
                }
            }}
            onClick={onClose}
        >
            <CloseIcon />
        </IconButton>
    </Stack>
);

export default GiftCardOption;